import { DispatchType, type DispatchesInfo } from '../types/businessTypes';
import type { Operation } from '../types/operation';

/**
 * True when there's more than one operation to choose from and the shopper
 * hasn't picked one yet — the item/fulfillment modal must ask first.
 */
export const isLocationSelectionRequired = (
  operations: Operation[],
  isLocationSelected: boolean,
) => operations.length > 1 && !isLocationSelected;

/**
 * True when the shopper is on the delivery flow but hasn't supplied a
 * delivery address yet — the item/fulfillment modal must ask first.
 */
export const isAddressSelectionRequired = (
  selectedDispatchType: DispatchType,
  dispatchesInfo: Partial<DispatchesInfo>,
) =>
  selectedDispatchType === DispatchType.DELIVERY &&
  !dispatchesInfo[DispatchType.DELIVERY]?.address;

/**
 * True when the operation can actually take an order for the shopper's
 * selected dispatch type right now. Drives whether an item can be added to
 * the cart, so item-level add-to-cart affordances agree with the global
 * "not accepting orders" status instead of ignoring it.
 *
 * Gates uniformly on the authoritative first-available slot for that
 * dispatch type, including delivery — matching BOB's
 * `availableDispatchTypes`/`isDispatchAvailable`, which never exempts
 * delivery from this check. A missing slot means the operation can't
 * currently take this kind of order.
 */
export const isAcceptingOrdersForDispatch = (
  operation: Operation | undefined,
  selectedDispatchType: DispatchType | undefined,
  dispatchesInfo: Partial<DispatchesInfo>,
): boolean => {
  if (!(operation?.enabled ?? false) || !selectedDispatchType) {
    return false;
  }

  return !!dispatchesInfo[selectedDispatchType]?.selectedTimeSlot;
};

/**
 * True when the operation can take an order for at least one dispatch type,
 * i.e. some configured dispatch type has a first-available time slot.
 * Drives the header's "Accepting orders" / "Not accepting orders" badge.
 */
export const isAcceptingOrders = (
  operation: Operation | undefined,
  dispatchesInfo: Partial<DispatchesInfo>,
): boolean =>
  (operation?.enabled ?? false) &&
  Object.values(dispatchesInfo).some((info) => !!info?.selectedTimeSlot);

/**
 * Dispatch types the operation can take an order for right now (see
 * {@link isAcceptingOrdersForDispatch}). Non-empty exactly when
 * {@link isAcceptingOrders} is true, i.e. when the header badge says
 * "Accepting orders".
 */
export const getAcceptingDispatchTypes = (
  operation: Operation | undefined,
  dispatchesInfo: Partial<DispatchesInfo>,
): DispatchType[] =>
  (Object.keys(dispatchesInfo) as DispatchType[]).filter((dispatchType) =>
    isAcceptingOrdersForDispatch(operation, dispatchType, dispatchesInfo),
  );
